import is from '@sindresorhus/is';
import type { InfiniteData } from '@tanstack/react-query';

import type {
    AgentChatMessage,
    AgentChatMessagePage,
    AssistantMessageEntry,
    AssistantMessageStatus,
    ChatAttachmentRef,
    ContextRefreshEnvelope,
    StructuredResponseEvent,
} from '../../../data-contract/chat';

export type MessagesInfiniteData = InfiniteData<AgentChatMessagePage, string | undefined>;

const PENDING_ASSISTANT_STATUS: AssistantMessageStatus = 'pending';

export const seedEmptyMessagesData = (): MessagesInfiniteData => ({
    pageParams: [undefined],
    pages: [{ messages: [], nextCursor: null }],
});

export const getStructuredEventBlockIds = (event: StructuredResponseEvent): readonly string[] =>
    event.payload.blocks.map((block) => block.blockId);

const getReplaceableStructuredEventBlockIds = (event: StructuredResponseEvent): readonly string[] =>
    event.payload.blocks.flatMap((block) =>
        block.type === 'activity-timeline' || block.type === 'status-update' ? [] : [block.blockId],
    );

const updateFirstPage = (
    data: MessagesInfiniteData | undefined,
    update: (messages: readonly AgentChatMessage[]) => readonly AgentChatMessage[],
): MessagesInfiniteData | undefined => {
    if (data === undefined) return data;
    const [firstPage, ...restPages] = data.pages;
    if (firstPage === undefined) return data;
    return { ...data, pages: [{ ...firstPage, messages: [...update(firstPage.messages)] }, ...restPages] };
};

const mapAllMessages = (
    data: MessagesInfiniteData | undefined,
    mapMessage: (message: AgentChatMessage) => AgentChatMessage,
): MessagesInfiniteData | undefined => {
    if (data === undefined) return data;
    return { ...data, pages: data.pages.map((page) => ({ ...page, messages: page.messages.map(mapMessage) })) };
};

export const prependUserMessage = (
    data: MessagesInfiniteData | undefined,
    message: AgentChatMessage,
): MessagesInfiniteData | undefined => updateFirstPage(data, (messages) => [message, ...messages]);

const buildPendingAssistantMessage = (messageId: string, createdAt: string): AgentChatMessage => ({
    createdAt,
    entries: [],
    id: messageId,
    role: 'assistant',
    status: PENDING_ASSISTANT_STATUS,
});

export const prependPendingAssistantMessage = (
    data: MessagesInfiniteData | undefined,
    messageId: string,
    createdAt: string,
): MessagesInfiniteData | undefined =>
    updateFirstPage(data, (messages) => {
        if (messages.some((existing) => existing.id === messageId)) return messages;
        return [buildPendingAssistantMessage(messageId, createdAt), ...messages];
    });

export const removeAssistantMessage = (
    data: MessagesInfiniteData | undefined,
    messageId: string,
): MessagesInfiniteData | undefined => {
    if (data === undefined) return data;
    return {
        ...data,
        pages: data.pages.map((page) => ({
            ...page,
            messages: page.messages.filter((message) => !(message.role === 'assistant' && message.id === messageId)),
        })),
    };
};

export const reconcileAssistantMessageStart = (
    data: MessagesInfiniteData | undefined,
    messageId: string,
    createdAt: string,
    optimisticMessageId: string | null = null,
): MessagesInfiniteData | undefined =>
    updateFirstPage(data, (messages) => {
        if (messages.some((message) => message.id === messageId && message.role === 'assistant')) {
            const isReplacedOptimisticMessage = (message: AgentChatMessage): boolean =>
                message.role === 'assistant' &&
                !is.null(optimisticMessageId) &&
                message.id === optimisticMessageId &&
                message.id !== messageId;

            return messages
                .filter((message) => !isReplacedOptimisticMessage(message))
                .map((message) =>
                    message.role === 'assistant' && message.id === messageId
                        ? { ...message, status: PENDING_ASSISTANT_STATUS }
                        : message,
                );
        }

        if (!is.null(optimisticMessageId)) {
            let replaced = false;
            const updated = messages.map((message): AgentChatMessage => {
                if (message.role === 'assistant' && message.id === optimisticMessageId) {
                    replaced = true;
                    return { ...message, createdAt, id: messageId, status: PENDING_ASSISTANT_STATUS };
                }
                return message;
            });
            if (replaced) return updated;
        }
        return [buildPendingAssistantMessage(messageId, createdAt), ...messages];
    });

const appendOrMergeText = (entries: readonly AssistantMessageEntry[], text: string): AssistantMessageEntry[] => {
    const last = entries[entries.length - 1];
    if (last?.type === 'text') {
        return [...entries.slice(0, -1), { text: last.text + text, type: 'text' }];
    }
    return [...entries, { text, type: 'text' }];
};

export const appendTextChunk = (
    data: MessagesInfiniteData | undefined,
    messageId: string,
    text: string,
): MessagesInfiniteData | undefined =>
    mapAllMessages(data, (message) => {
        if (message.id !== messageId || message.role !== 'assistant') return message;
        return { ...message, entries: appendOrMergeText(message.entries, text) };
    });

const entryHasOverlappingBlockId = (entry: AssistantMessageEntry, blockIds: ReadonlySet<string>): boolean =>
    entry.type === 'event' && getReplaceableStructuredEventBlockIds(entry.envelope).some((id) => blockIds.has(id));

// A stream resume or a refetch racing the live stream redelivers events that are already cached.
// The redelivered payload can be newer than the cached one (e.g. a status update that finished),
// so it takes that entry's place rather than being appended under a duplicate event id.
const upsertEntryByEventId = (
    entries: readonly AssistantMessageEntry[],
    incoming: Exclude<AssistantMessageEntry, { type: 'text' }>,
): AssistantMessageEntry[] => {
    const index = entries.findIndex(
        (entry) => entry.type !== 'text' && entry.envelope.eventId === incoming.envelope.eventId,
    );
    return index === -1 ? [...entries, incoming] : entries.with(index, incoming);
};

export const appendStructuredEvent = (
    data: MessagesInfiniteData | undefined,
    messageId: string,
    event: StructuredResponseEvent,
): MessagesInfiniteData | undefined => {
    const incomingBlockIds = new Set(getReplaceableStructuredEventBlockIds(event));
    const incoming: AssistantMessageEntry = { envelope: event, type: 'event' };
    return mapAllMessages(data, (message) => {
        if (message.role !== 'assistant') return message;
        const { entries } = message;
        const kept = entries.filter((entry) => !entryHasOverlappingBlockId(entry, incomingBlockIds));
        if (message.id !== messageId) {
            return kept.length === entries.length ? message : { ...message, entries: kept };
        }
        return { ...message, entries: upsertEntryByEventId(kept, incoming) };
    });
};

export const appendContextRefreshEvent = (
    data: MessagesInfiniteData | undefined,
    messageId: string,
    event: ContextRefreshEnvelope,
): MessagesInfiniteData | undefined =>
    mapAllMessages(data, (message) => {
        if (message.role !== 'assistant' || message.id !== messageId) return message;
        return {
            ...message,
            entries: upsertEntryByEventId(message.entries, { envelope: event, type: 'context-refresh' }),
        };
    });

const markRefDeleted = (
    refs: readonly ChatAttachmentRef[],
    attachmentId: string,
    deletedAt: string,
): readonly ChatAttachmentRef[] | null => {
    let changed = false;
    const next = refs.map((ref) => {
        if (ref.attachmentId !== attachmentId || is.nonEmptyString(ref.deletedAt)) return ref;
        changed = true;
        return { ...ref, deletedAt };
    });
    return changed ? next : null;
};

// Deletion must reach fresh threads too, where the messages query is disabled and an
// invalidation never refetches — so the tombstone is written straight into the cache.
export const markAttachmentDeleted = (
    data: MessagesInfiniteData | undefined,
    attachmentId: string,
    deletedAt: string,
): MessagesInfiniteData | undefined =>
    mapAllMessages(data, (message) => {
        if (message.role !== 'user') return message;
        if (message.messageType === 'text' && message.attachments !== undefined) {
            const next = markRefDeleted(message.attachments, attachmentId, deletedAt);
            return next === null ? message : { ...message, attachments: [...next] };
        }
        if (message.messageType === 'create-jsa-entities' && message.createJsaEntities.attachments !== undefined) {
            const next = markRefDeleted(message.createJsaEntities.attachments, attachmentId, deletedAt);
            return next === null
                ? message
                : { ...message, createJsaEntities: { ...message.createJsaEntities, attachments: [...next] } };
        }
        return message;
    });

export const setAssistantMessageStatus = (
    data: MessagesInfiniteData | undefined,
    messageId: string,
    status: AssistantMessageStatus,
): MessagesInfiniteData | undefined =>
    mapAllMessages(data, (message) =>
        message.id === messageId && message.role === 'assistant' ? { ...message, status } : message,
    );
